import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/services", label: "Practice areas", id: "services" },
  { to: "/how-it-works", label: "How it works", id: "how" },
  { to: "/blog", label: "Insights", id: "blog" },
  { to: "/about", label: "About", id: "about" },
  { to: "/attorneys", label: "For attorneys", id: "attorneys" },
];

export function Wordmark({ className = "" }) {
  return (
    <span className={`flex items-center gap-2.5 ${className}`}>
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#D9B36C]/60 bg-[#14161D]">
        <span className="font-display text-[#D9B36C] text-lg leading-none translate-y-[1px]">U</span>
      </span>
      <span className="font-display text-[1.35rem] text-[#F4F1EA] leading-none">
        Upload <span className="italic text-[#D9B36C]">Counsel</span>
      </span>
    </span>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-colors ${
        scrolled || open ? "bg-[#0B0C10]/90 border-[#272B36]" : "bg-[#0B0C10]/50 border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-brand" aria-label="Upload Counsel home">
          <Wordmark />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Main">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? "text-[#F4F1EA]" : "text-slate-400 hover:text-[#F4F1EA]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/start"
            data-testid="nav-cta-consultation"
            className="hidden sm:inline-flex items-center bg-[#D9B36C] text-[#0B0C10] font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-[#E5C688] transition-colors"
          >
            Start a request
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 -mr-2 text-[#F4F1EA]"
            onClick={() => setOpen((s) => !s)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#272B36] bg-[#0B0C10]">
          <div className="px-5 py-4 flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mnav-link-${l.id}`}
                className={({ isActive }) =>
                  `py-3 border-b border-[#272B36]/60 ${isActive ? "text-[#F4F1EA]" : "text-slate-300"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/start"
              data-testid="mnav-cta"
              className="mt-4 inline-flex justify-center bg-[#D9B36C] text-[#0B0C10] font-semibold px-5 py-3 rounded-full"
            >
              Start a request
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
